/**
 * Row-height contract for data-grid rows — how tall a row may grow, and what
 * the virtualizer assumes about a row it has not measured yet.
 *
 * A desktop row holds single-line cells and stays uniform. A mobile row holds
 * a card whose height follows its own content — a wrapped structure name, a
 * metadata line, a row of job actions, a customer logo — so mobile rows
 * measure themselves and publish an estimate the virtualizer reserves scroll
 * space with while a page is still in flight.
 *
 * Contains:
 * - LAZY_ROW_HEIGHT, DESKTOP_ESTIMATED_ROW_HEIGHT, MOBILE_MIN_ROW_HEIGHT,
 *   MOBILE_ESTIMATED_ROW_HEIGHT — the height constants
 * - resolveRowHeightProps — pure grid-mode → DataGridPro height props
 *
 * Returns plain JS — no JSX.
 */

// A desktop lazy grid pins every row to one height. Viewport lazy loading only
// fetches the next page once scrolling reveals a stable block of skeleton
// rows; an unmeasured 'auto' skeleton collapses, row positions re-flow, and
// the fetch trigger never fires.
export const LAZY_ROW_HEIGHT = 52;

/** Pre-measurement height of a desktop auto-height row. */
export const DESKTOP_ESTIMATED_ROW_HEIGHT = 52;

// A mobile card never measures shorter than this — skeleton rows included —
// so a lazy grid still reserves the scroll overflow its fetch trigger needs
// even though its rows size themselves.
export const MOBILE_MIN_ROW_HEIGHT = 76;

/** Pre-measurement height of a mobile card carrying a name, a secondary line and one metadata row. */
export const MOBILE_ESTIMATED_ROW_HEIGHT = 104;

const AUTO_ROW_HEIGHT = () => 'auto';
const DESKTOP_ESTIMATE = () => DESKTOP_ESTIMATED_ROW_HEIGHT;
const MOBILE_ESTIMATE = () => MOBILE_ESTIMATED_ROW_HEIGHT;

/**
 * Resolves the height props a grid spreads onto DataGridPro.
 *
 * Mobile always measures: a card is content-sized, and a fixed inline row
 * height clips it with no CSS recourse (the grid writes the height onto the
 * row element itself, so a `maxHeight` override on the cell cannot grow it).
 * The fixed height a lazy grid's fetch trigger depends on is a desktop-only
 * concern; on mobile the card's own minimum height reserves that overflow.
 *
 * @param {object} params
 * @param {boolean} [params.lazyLoading=false] - Grid is in viewport lazy-loading mode.
 * @param {boolean} [params.isMobile=false] - Grid renders the single-column card layout.
 * @returns {{rowHeight?: number, getRowHeight?: Function, getEstimatedRowHeight?: Function}}
 *   Height props to spread onto DataGridPro. Never carries both `rowHeight`
 *   and `getRowHeight` — DataGridPro treats a fixed height as authoritative.
 */
export function resolveRowHeightProps({ lazyLoading = false, isMobile = false } = {}) {
  if (isMobile) return { getRowHeight: AUTO_ROW_HEIGHT, getEstimatedRowHeight: MOBILE_ESTIMATE };
  if (lazyLoading) return { rowHeight: LAZY_ROW_HEIGHT };
  return { getRowHeight: AUTO_ROW_HEIGHT, getEstimatedRowHeight: DESKTOP_ESTIMATE };
}
